import { useState, useMemo } from 'react';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  FileText,
  Clock,
} from 'lucide-react';
import { useDagCounts } from '../../hooks/useFileIntegrity';

const STAGE_COLUMNS = [
  { countKey: 'source_record_count', deltaKey: null, label: 'Source' },
  { countKey: 'stg_record_count', deltaKey: 'source_to_stg_delta', label: 'Staging' },
  { countKey: 'int_record_count', deltaKey: 'stg_to_int_delta', label: 'Intermediate' },
  { countKey: 'mart_record_count', deltaKey: 'int_to_mart_delta', label: 'Mart' },
];

const FILE_TYPE_LABELS = {
  eligibility: 'Eligibility',
  action_completion: 'Action Completion',
  deposit: 'Deposit',
  cohort: 'Cohort',
};

function formatNumber(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString();
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
}

function DeltaBadge({ value }) {
  if (value == null) return <span className="text-gray-300">-</span>;
  // Deltas are stored as (prior_stage - current_stage), so negate to show losses as negative
  const num = -Number(value);
  if (num === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" /> 0
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
      <AlertTriangle className="w-3 h-3" /> {num > 0 ? '+' : ''}{formatNumber(num)}
    </span>
  );
}

function PartnerSummaryCard({ partnerLabel, fileType, latestRow }) {
  const hasMartTracking = latestRow.source_to_mart_delta != null;
  const totalDelta = hasMartTracking ? -Number(latestRow.source_to_mart_delta) : null;
  const hasLoss = hasMartTracking && totalDelta !== 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{partnerLabel}</h3>
          <p className="text-xs text-gray-500 mt-0.5">Latest file received</p>
        </div>
        {hasMartTracking ? (
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
            hasLoss ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'
          }`}>
            {hasLoss ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
            {hasLoss ? `${totalDelta > 0 ? '+' : ''}${formatNumber(totalDelta)} delta` : 'No loss'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            Inbound only
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="truncate font-mono text-xs" title={latestRow.source_file_name}>
            {latestRow.source_file_name}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span>{formatDateTime(latestRow.file_datetime)}</span>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-2 text-center">
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Source</div>
            <div className="text-sm font-semibold text-gray-900 tabular-nums">
              {formatNumber(latestRow.source_record_count)}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Mart</div>
            <div className="text-sm font-semibold text-gray-900 tabular-nums">
              {formatNumber(latestRow.mart_record_count)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const FileIntegrity = () => {
  const { data, isLoading, isError, error, refetch, isFetching } = useDagCounts();
  const [sortField, setSortField] = useState('file_datetime');
  const [sortDir, setSortDir] = useState('desc');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');

  const rows = data || [];

  const partnerSummaries = useMemo(() => {
    const grouped = {};
    for (const row of rows) {
      const key = `${row.file_type}|${row.partner_label}`;
      if (!grouped[key] || new Date(row.file_datetime) > new Date(grouped[key].file_datetime)) {
        grouped[key] = row;
      }
    }
    return Object.entries(grouped)
      .map(([key, row]) => {
        const [fileType, partnerLabel] = key.split('|');
        return { key, fileType, partnerLabel, row };
      })
      .sort((a, b) => a.fileType.localeCompare(b.fileType) || a.partnerLabel.localeCompare(b.partnerLabel));
  }, [rows]);

  const fileTypes = useMemo(
    () => [...new Set(rows.map((r) => r.file_type))].sort(),
    [rows]
  );

  const partnerLabels = useMemo(
    () => [...new Set(rows.map((r) => r.partner_label))].sort(),
    [rows]
  );

  const sortedRows = useMemo(() => {
    let filtered = rows;
    if (fileTypeFilter !== 'all') filtered = filtered.filter((r) => r.file_type === fileTypeFilter);
    if (partnerFilter !== 'all') filtered = filtered.filter((r) => r.partner_label === partnerFilter);
    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'file_datetime') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      } else {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortField, sortDir, partnerFilter, fileTypeFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        <span className="ml-2 text-gray-500">Loading pipeline data...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-800 font-medium">Failed to load pipeline data</p>
        <p className="text-red-600 text-sm mt-1">{error?.message}</p>
        <button
          onClick={() => refetch()}
          className="mt-3 px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">File Integrity</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track arrival and integrity of inbound client files across the data pipeline
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards — grouped by file type */}
      {partnerSummaries.length > 0 && (() => {
        const grouped = {};
        for (const s of partnerSummaries) {
          (grouped[s.fileType] ||= []).push(s);
        }
        return Object.entries(grouped)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([fileType, items]) => (
            <div key={fileType} className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                {FILE_TYPE_LABELS[fileType] || fileType}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map(({ key, partnerLabel, row }) => (
                  <PartnerSummaryCard key={key} partnerLabel={partnerLabel} fileType={fileType} latestRow={row} />
                ))}
              </div>
            </div>
          ));
      })()}

      {/* Detail Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">All Files</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{sortedRows.length} files</span>
            <select
              value={fileTypeFilter}
              onChange={(e) => setFileTypeFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All File Types</option>
              {fileTypes.map((ft) => (
                <option key={ft} value={ft}>{FILE_TYPE_LABELS[ft] || ft}</option>
              ))}
            </select>
            <select
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Partners</option>
              {partnerLabels.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                </th>
                {STAGE_COLUMNS.map((col) => (
                  <th
                    key={col.countKey}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            {sortedRows.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No pipeline data found
                  </td>
                </tr>
              </tbody>
            ) : (
              sortedRows.map((row, idx) => {
                const hasMartTracking = row.source_to_mart_delta != null;
                const totalDelta = hasMartTracking ? -Number(row.source_to_mart_delta) : null;
                const hasLoss = hasMartTracking && totalDelta !== 0;
                return (
                  <tbody key={`${row.source_file_name}-${idx}`} className="border-b border-gray-200">
                    <tr className="bg-gray-50/60">
                      <td colSpan={5} className="px-4 pt-3 pb-1">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                            {FILE_TYPE_LABELS[row.file_type] || row.file_type}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{row.partner_label}</span>
                          <span className="text-gray-300">|</span>
                          <span className="text-sm text-gray-500 whitespace-nowrap">{formatDateTime(row.file_datetime)}</span>
                          <span className="text-gray-300">|</span>
                          <span className="text-sm font-mono text-gray-600 truncate flex-1 min-w-0" title={row.source_file_name}>
                            {row.source_file_name}
                          </span>
                          {hasLoss && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 flex-shrink-0">
                              <AlertTriangle className="w-3 h-3" /> {totalDelta > 0 ? '+' : ''}{formatNumber(totalDelta)} total delta
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Count
                      </td>
                      {STAGE_COLUMNS.map((col) => (
                        <td key={col.countKey} className="px-4 py-2 text-center text-sm font-semibold text-gray-900 tabular-nums">
                          {formatNumber(row[col.countKey])}
                        </td>
                      ))}
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-2 pb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Change
                      </td>
                      {STAGE_COLUMNS.map((col) => (
                        <td key={`delta-${col.countKey}`} className="px-4 py-2 pb-3 text-center">
                          <DeltaBadge value={col.deltaKey ? row[col.deltaKey] : null} />
                        </td>
                      ))}
                    </tr>
                  </tbody>
                );
              })
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default FileIntegrity;
